import { Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  API,
  BookingDTO,
  BookingRecalculationDTO,
  MarkerDTO,
  MarkerFullDTO,
  MediumFullDTO,
  MediumGroupDTO,
  MediumGroupFullDTO,
  NamedIdentityDTO,
  RequirementFullDTO,
  TemplateDataSetDTO,
  TemplateDataSetEntryDTO,
} from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { MediumGroupsComponent } from '../../../media/medium-groups/medium-groups.component';
import { BookingService } from '../../../../services/booking.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from 'src/app/services/util.service';
import { SbMarkerListComponent } from '../../../../components/sb-marker-list/sb-marker-list.component';
import { ToasterService, ToastType } from '../../../../services/toaster/toaster.service';
import { PermissionService } from '../../../../services/permission.service';
import { SbTemplateDataSetSelectModalComponent } from './template-source-select-modal/sb-template-data-set-select-modal.component';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-comp-requirement-booking-assignment',
  templateUrl: './requirement-booking-assignment.component.html',
  styleUrls: ['./requirement-booking-assignment.component.scss'],
  standalone: false,
})
export class RequirementBookingAssignmentComponent implements OnInit {
  bookingPreviewData: BookingDTO[];
  @Input() customer: NamedIdentityDTO;
  @Input({ required: true }) requirementData: RequirementFullDTO;
  @Input() planningId: number;
  @Input() isInIntegratedContext: boolean;
  @Input() readonlyCommercial: boolean;
  @Input() duration: number;
  // TODO CHECK FOR PLANNING ID
  @Output() bookingsChanged: EventEmitter<BookingDTO[]> = new EventEmitter();
  @Output() loadingChanged: EventEmitter<boolean> = new EventEmitter();
  @Output() requirementDataChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('disableBookingToggle') disableBookingToggle: ElementRef;

  selectedMotiveGroup: MediumGroupFullDTO;
  selectedTemplateDataset: TemplateDataSetDTO;
  selectedTemplateDatasetEntry: TemplateDataSetEntryDTO;
  loading = false;
  showDeprecatedBookings: boolean;
  showExcludedBookings: boolean;
  overrideAssigned: boolean;
  overrideExcluded: boolean;
  hasTemplateDataSetColumn: boolean;

  constructor(
    public readonly modal: ModalService,
    private readonly api: API,
    public readonly errorHandler: ErrorService,
    public readonly bookingService: BookingService,
    public readonly translate: TranslateService,
    public readonly util: UtilService,
    public readonly toastHandler: ToasterService,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef
  ) {
    this.bookingsChanged = new EventEmitter();
    this.loadingChanged = new EventEmitter();
    this.selectedMotiveGroup = undefined;
    this.selectedTemplateDataset = undefined;
    this.selectedTemplateDatasetEntry = undefined;
    this.loading = false;
    this.showDeprecatedBookings = false;
    this.showExcludedBookings = false;
    this.overrideAssigned = false;
    this.overrideExcluded = false;
    this.hasTemplateDataSetColumn = false;
    this.bookingPreviewData = [];
  }

  ngOnInit() {
    if (!this.requirementData) {
      throw Error('Missing requirement for booking assignment');
    }
    this.bookingService.createBookings([this.requirementData], this.planningId ? this.planningId : null).then(r => {
      this.bookingPreviewData = r.get(0) ?? [];
      this.hasTemplateDataSetColumn = this.bookingPreviewData.some(booking => booking.templateDataSet || booking.medium?.templateGroup);
      this.bookingService.countMissingBookings(this.bookingPreviewData);
      this.bookingsChanged.emit(this.bookingPreviewData);
    });
  }

  showMarkerList() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: SbMarkerListComponent, data: { requirementId: this.requirementData.id } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: MarkerFullDTO) => {
        if (!result) {
          return;
        }
        if (this.planningId) {
          this.api
            .ds_cm_Requirement_createManualBookingsForRequirementPlanning(this.requirementData.id, this.planningId, [result as MarkerDTO])
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: bookingRecalculation => {
                this.handleBookingRefresh(bookingRecalculation);
              },
            });
        } else {
          this.api
            .ds_cm_Requirement_createManualBookingsForRequirement(this.requirementData.id, [result as MarkerDTO])
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: bookingRecalculation => {
                this.handleBookingRefresh(bookingRecalculation);
              },
            });
        }
      })
      .catch(() => {});
  }

  selectTemplateDataSet($event: MouseEvent, booking: BookingDTO) {
    $event.stopPropagation();
    $event.preventDefault();
    this.modal
      .showNavigationModal({
        entryComponent: {
          value: SbTemplateDataSetSelectModalComponent,
          data: { templateGroupID: booking?.medium?.templateGroup?.id ?? undefined },
        },
        title: this.translate.instant('lang.requirementBookingAssignment.selectTemplateDataset'),
        modalOptions: {
          size: 'xl',
        },
      })
      .then((result: { templateDataset: TemplateDataSetDTO; templateDatasetEntry: TemplateDataSetEntryDTO | null } | undefined) => {
        if (result && booking) {
          booking.templateDataSet = result.templateDataset;
          booking.templateDataSetEntry = result.templateDatasetEntry;
          this.hasTemplateDataSetColumn = true;
          this.toastHandler.toast(ToastType.SUCCESS, this.translate.instant('lang.requirementBookingAssignment.templateDatasetAssigned'));
          this.bookingsChanged.emit(this.bookingPreviewData);
        }
      })
      .catch(() => {});
  }

  noExcludedBookings() {
    return this.translate.instant('lang.requirementBookingAssignment.noExcludedBookings');
  }

  noDeprecatedBookings() {
    return this.translate.instant('lang.requirementBookingAssignment.noDeprecatedBookings');
  }

  handleBookingRefresh(data: BookingRecalculationDTO): void {
    if (data.created?.length > 0) {
      for (const e of data.created) {
        this.bookingPreviewData.push(e);
      }
    }
    if (data.restored?.length > 0) {
      for (const e of data.restored) {
        this.filterDeprecated(this.bookingPreviewData, e.id);
        this.bookingPreviewData.push(e);
      }
    }
    if (data.deprecated?.length > 0) {
      for (const e of data.deprecated) {
        this.filterDeprecated(this.bookingPreviewData, e.id);
        this.bookingPreviewData.push(e);
      }
    }
    if (data.unmodified?.length > 0) {
      for (const e of data.unmodified) {
        this.filterDeprecated(this.bookingPreviewData, e.id);
        this.bookingPreviewData.push(e);
      }
    }
    this.hasTemplateDataSetColumn = this.bookingPreviewData.some(i => i.templateDataSet || i.medium?.templateGroup);
  }

  filterDeprecated(previousBookings: BookingDTO[], searchVal: number) {
    if (previousBookings && previousBookings.length > 0) {
      const bookingForDelete = previousBookings.findIndex(x => x.id === searchVal);
      if (bookingForDelete !== -1) {
        previousBookings.splice(bookingForDelete, 1);
      }
    }
  }

  refreshBookings(data: BookingDTO[]) {
    this.bookingPreviewData = data;
    this.hasTemplateDataSetColumn = data.some(i => i.templateDataSet || i.medium?.templateGroup);
    this.bookingsChanged.emit(this.bookingPreviewData);
    this.loadingChanged.emit(this.loading);
  }

  setMediumGroup() {
    this.bookingService
      .setMediumGroup(
        this.selectedMotiveGroup.id,
        this.bookingPreviewData,
        {
          overrideAssigned: this.overrideAssigned,
          overrideExcluded: this.overrideExcluded,
        },
        this.planningId ? this.planningId : null
      )
      .then((data: BookingDTO[]) => {
        this.refreshBookings(data);
      });
  }

  openMediaModal($event: any, pBooking: BookingDTO) {
    $event.stopPropagation();
    $event.preventDefault();
    this.modal
      .modal(
        {
          title: 'Media Explorer ',
          modalOptions: { windowClass: 'bigXL' },
        },
        {
          templateId: 'mediaexplorermodal',
        },
        { booking: pBooking, startSearch: true } // <--- payload
      )
      .then((result: MediumFullDTO) => {
        if (result) {
          if (pBooking?.templateDataSet) {
            this.removeTemplateDataset(pBooking);
          }
          pBooking.medium = result;
          this.bookingService.editBookings(this.bookingPreviewData, this.planningId ? this.planningId : null).then((r: BookingDTO[]) => {
            this.refreshBookings(r);
          });
        }
      })
      .catch(() => {
        this.modal.dismissAll();
      });
  }

  getClass(medium: MediumFullDTO): string {
    if (medium) {
      return 'btn-secondary';
    } else {
      return 'btn-primary';
    }
  }

  toggleDisableBooking(e: any, booking: BookingDTO) {
    if (booking.medium) {
      this.modal
        .confirm({
          title: this.translate.instant('lang.requirementBookingAssignment.removeMotiveFromBooking'),
          message: this.translate.instant('lang.requirementBookingAssignment.removeMotiveFromBookingQuestion'),
          yes: this.translate.instant('lang.globals.yes'),
          no: this.translate.instant('lang.globals.no'),
        })
        .then((result: boolean) => {
          if (result) {
            this.removeMotive(booking);
            booking.excluded = e.target.checked;
          } else {
            e.target.checked = false;
          }
        });
    } else {
      booking.excluded = e.target.checked;
    }
  }

  disableMissingBookings() {
    for (const booking of this.bookingPreviewData) {
      if (!booking.medium) {
        booking.excluded = true;
      }
    }
  }

  disableAllBookings() {
    for (const booking of this.bookingPreviewData) {
      booking.excluded = true;
    }
  }

  removeMotive(booking: BookingDTO) {
    booking.medium = null;
    this.bookingsChanged.emit(this.bookingPreviewData);
  }

  removeTemplateDataset(booking: BookingDTO) {
    booking.templateDataSet = null;
    this.bookingsChanged.emit(this.bookingPreviewData);
  }

  selectTemplateDataSetOverride(mainMediumGroup: boolean = false) {
    this.modal
      .showNavigationModal({
        entryComponent: {
          value: SbTemplateDataSetSelectModalComponent,
          data: { templateGroupID: this.selectedTemplateDataset?.group?.id ?? undefined },
        },
        title: this.translate.instant('lang.requirementBookingAssignment.selectTemplateDataset'),
        modalOptions: {
          size: 'xl',
        },
      })
      .then((result: { templateDataset: TemplateDataSetDTO; templateDatasetEntry: TemplateDataSetEntryDTO | null } | undefined) => {
        if (result) {
          this.selectedTemplateDataset = result.templateDataset;
          this.selectedTemplateDatasetEntry = result.templateDatasetEntry;

          this.bookingPreviewData.forEach(booking => {
            if (booking?.medium?.templateGroup) {
              booking.templateDataSet = result.templateDataset;
              booking.templateDataSetEntry = result.templateDatasetEntry;
            }
          });
          this.hasTemplateDataSetColumn = true;
          this.toastHandler.toast(ToastType.SUCCESS, this.translate.instant('lang.requirementBookingAssignment.templateDatasetAssigned'));
          this.bookingsChanged.emit(this.bookingPreviewData);

          if (mainMediumGroup) {
            this.requirementData.assignedTemplateDataSet = result.templateDataset;
            this.requirementData.assignedTemplateDataSetEntry = result.templateDatasetEntry;
            this.requirementDataChanged.emit(true);
          }
        }
      })
      .catch(() => {});
  }

  selectMotiveGroup(mainMediumGroup: boolean = false) {
    this.modal
      .showNavigationModal({
        entryComponent: {
          value: MediumGroupsComponent,
          data: { customer: this.customer, startSearch: true },
        },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: MediumGroupFullDTO) => {
        if (result) {
          this.selectedMotiveGroup = result;
          if (
            this.bookingService.countExcludedBookings(this.bookingPreviewData) > 0 ||
            this.bookingService.countFilledBookings(this.bookingPreviewData) > 0
          ) {
            this.showOverrideOptions(result);
          } else {
            this.setMediumGroup();
          }
          if (mainMediumGroup) {
            this.requirementData.assignedMediumGroup = result;
            this.requirementDataChanged.emit(true);
          }
        }
      })
      .catch(() => {});
  }

  removeMainMediumGroup() {
    this.requirementData.assignedMediumGroup = null;
    if (!this.requirementData.assignedTemplateDataSet) {
      this.requirementData.autoBook = false;
    }
    this.requirementDataChanged.emit(true);
  }

  removeMainTemplateDataSet() {
    this.requirementData.assignedTemplateDataSet = null;
    if (!this.requirementData.assignedMediumGroup) {
      this.requirementData.autoBook = false;
    }
    this.requirementDataChanged.emit(true);
  }

  showOverrideOptions(mediumGroup: MediumGroupDTO) {
    this.modal
      .mediumGroupOverride(
        {
          overrideAssigned: false,
          overrideDeprecated: false,
          overrideExcluded: false,
          excludeNotAssigned: true,
        },
        this.bookingPreviewData
      )
      .then(result => {
        this.bookingService
          .setMediumGroup(mediumGroup.id, this.bookingPreviewData, result, this.planningId ? this.planningId : null)
          .then((r: BookingDTO[]) => {
            this.refreshBookings(r);
          })
          .catch(() => {});
      });
  }

  removeMotiveAsk(booking: BookingDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.requirementBookingAssignment.removeMotiveFromBooking'),
        message: this.translate.instant('lang.requirementBookingAssignment.removeMotiveFromBookingQuestion'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          if (booking.templateDataSet) {
            this.removeTemplateDataset(booking);
          }
          this.removeMotive(booking);
        }
      });
  }

  disableBookingsAsk(mode?: string) {
    let modalMessage: string;
    let modalTitle: string;

    if (mode === 'excludeAll') {
      modalTitle = this.translate.instant('lang.requirementBookingAssignment.excludeAllBookings');
      modalMessage = this.translate.instant('lang.requirementBookingAssignment.excludeAllBookingsQuestion');
    } else {
      modalTitle = this.translate.instant('lang.requirementBookingAssignment.excludeBookingsWithoutMotive');
      modalMessage = this.translate.instant('lang.requirementBookingAssignment.excludeBookingsWithoutMotiveQuestion');
    }
    this.modal
      .confirm({
        title: modalTitle,
        message: modalMessage,
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          if (mode === 'excludeAll') {
            this.disableAllBookings();
          } else {
            this.disableMissingBookings();
          }
        }
      });
  }

  durationCheck(medium: MediumFullDTO) {
    if (!medium) {
      return null;
    }
    if (!medium?.duration) {
      return null;
    }
    if (medium.duration > this.duration + 0.5 || medium.duration < this.duration - 0.5) {
      return true;
    }
    return null;
  }

  toggleAutoBook() {
    this.requirementData.autoBook = !this.requirementData.autoBook;
    this.requirementDataChanged.emit(true);
  }

  removeTemplateAsk(booking: BookingDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.requirementBookingAssignment.removeTemplateFromBooking'),
        message: this.translate.instant('lang.requirementBookingAssignment.removeTemplateFromBookingQuestion'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.removeTemplateDataset(booking);
        }
      });
  }

  getAssignedTemplateDataSetDisabled(): boolean {
    return !this.bookingPreviewData.some(booking => booking?.medium?.templateGroup);
  }

  hasDifferentTemplateGroups(): boolean {
    const bookingsWithTemplateGroups = this.bookingPreviewData.filter(booking => booking?.medium?.templateGroup);
    if (bookingsWithTemplateGroups.length <= 1) {
      return false;
    }
    const firstElement = bookingsWithTemplateGroups[0];
    return !bookingsWithTemplateGroups.every(booking => booking?.medium?.templateGroup?.id === firstElement.medium.templateGroup?.id);
  }
}
